const ACCOUNT_FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const DIALOG_TABINDEX_ATTR = "data-dbi-account-prev-tabindex";
const ACCOUNT_HOST_TABINDEX_ATTR = "data-dbi-account-host-tabindex";
const RESTORE_RETRY_DELAYS = [0, 16, 50, 100, 150, 250, 400];
const accountFocusState = new WeakMap();

function getAccountState(account) {
  if (!accountFocusState.has(account)) {
    accountFocusState.set(account, {
      opener: null,
      focusTimer: null,
      focusRaf: null,
      restoreTimers: [],
      restoringFocus: false,
      shadowCloseBound: false,
      dialogWatchBound: false,
    });
  }

  return accountFocusState.get(account);
}

function getAccountShadowRoot(account) {
  return account.shadowRoot || null;
}

function findAccountDialog(root) {
  return root.querySelector('[role="dialog"]') || root.querySelector("[aria-modal]");
}

function isVisibleElement(element) {
  if (!element) return false;

  const style = getComputedStyle(element);
  return (
    style.display !== "none" &&
    style.visibility !== "hidden" &&
    element.getClientRects().length > 0
  );
}

function hasVisibleAccountIframe(root) {
  if (!root) return false;

  return Array.from(root.querySelectorAll("iframe")).some(isVisibleElement);
}

function isAccountSheetVisible(account) {
  const root = getAccountShadowRoot(account);
  if (!root) return false;

  const dialog = findAccountDialog(root);
  if (isVisibleElement(dialog)) return true;

  // Orders/Profile replace the menu dialog with an embedded account iframe.
  return hasVisibleAccountIframe(root);
}

function isAccountDialogVisible(account) {
  return isAccountSheetVisible(account);
}

function isAccountDialogActive(account) {
  return (
    account.dataset.dbiAccountOpen === "true" && isAccountSheetVisible(account)
  );
}

function isNodeConnected(node) {
  if (!(node instanceof Node)) return false;

  const root = node.getRootNode();
  if (root === document) return document.documentElement.contains(node);
  if (root instanceof ShadowRoot) return root.host.isConnected;

  return false;
}

function getAccountTrigger(account) {
  const root = getAccountShadowRoot(account);
  if (!root) return null;

  const dialog = findAccountDialog(root);
  const partTrigger = root.querySelector('[part="signed-out-avatar"]');
  if (partTrigger) return partTrigger;

  return (
    Array.from(root.querySelectorAll("button")).find(
      (button) => !dialog?.contains(button),
    ) || null
  );
}

function getAccountFocusTarget(account, preferredOpener) {
  if (preferredOpener && isNodeConnected(preferredOpener)) {
    return preferredOpener;
  }

  const trigger = getAccountTrigger(account);
  if (trigger && isNodeConnected(trigger)) return trigger;

  if (!(account instanceof HTMLElement) || !account.isConnected) return null;

  if (!account.hasAttribute(ACCOUNT_HOST_TABINDEX_ATTR)) {
    account.setAttribute(
      ACCOUNT_HOST_TABINDEX_ATTR,
      account.getAttribute("tabindex") ?? "",
    );
    account.setAttribute("tabindex", "-1");
  }

  return account;
}

function findDialogCloseButton(root) {
  const dialog = findAccountDialog(root);
  if (!dialog) return null;

  const labeled = dialog.querySelector(
    'button[aria-label*="lose" i], button[aria-label*="Close" i]',
  );
  if (labeled) return labeled;

  return (
    Array.from(dialog.querySelectorAll("button")).find((button) => {
      const label = `${button.getAttribute("aria-label") || ""} ${button.textContent || ""}`;
      return /close|×|✕/i.test(label.trim());
    }) || null
  );
}

function getVisibleFocusables(container) {
  return Array.from(container.querySelectorAll(ACCOUNT_FOCUSABLE_SELECTOR)).filter(
    (element) =>
      element.getClientRects().length > 0 &&
      getComputedStyle(element).visibility !== "hidden",
  );
}

function getAccountDialogFocusables(account) {
  const root = getAccountShadowRoot(account);
  if (!root) return [];

  const dialog = findAccountDialog(root);
  if (!dialog) return [];

  return getVisibleFocusables(dialog);
}

function isFocusInsideAccountDialog(account, element) {
  if (!(element instanceof Element)) return false;

  const root = getAccountShadowRoot(account);
  if (!root) return false;

  const dialog = findAccountDialog(root);
  if (dialog?.contains(element)) return true;

  if (element.tagName === "IFRAME" && root.contains(element)) return true;

  return false;
}

function walkFocusAncestors(element, callback) {
  let node = element;

  while (node) {
    callback(node);

    if (node.parentElement) {
      node = node.parentElement;
      continue;
    }

    const root = node.getRootNode();
    if (root instanceof ShadowRoot && root.host) {
      node = root.host;
      continue;
    }

    break;
  }
}

function prepareOpenerForFocus(element) {
  walkFocusAncestors(element, (node) => {
    if (!(node instanceof HTMLElement)) return;

    node.inert = false;

    if (node.hasAttribute("aria-hidden")) {
      node.removeAttribute("aria-hidden");
    }
  });
}

function isFocusOnTarget(target, account) {
  const active = document.activeElement;
  if (!active) return false;
  if (active === target) return true;
  if (target.contains(active)) return true;

  const trigger = getAccountTrigger(account);
  if (
    target === account &&
    trigger &&
    (active === trigger || trigger.contains(active))
  ) {
    return true;
  }

  return false;
}

function cancelPendingFocus(account) {
  const state = getAccountState(account);

  if (state.focusTimer) {
    clearTimeout(state.focusTimer);
    state.focusTimer = null;
  }

  if (state.focusRaf) {
    cancelAnimationFrame(state.focusRaf);
    state.focusRaf = null;
  }
}

function cancelPendingRestore(account) {
  const state = getAccountState(account);

  cancelPendingFocus(account);
  state.restoreTimers.forEach((timer) => clearTimeout(timer));
  state.restoreTimers = [];
  state.restoringFocus = false;
}

function setDialogFocusablesTabbable(account, tabbable) {
  const root = getAccountShadowRoot(account);
  const dialog = root && findAccountDialog(root);
  if (!dialog) return;

  Array.from(dialog.querySelectorAll(ACCOUNT_FOCUSABLE_SELECTOR)).forEach(
    (element) => {
      if (tabbable) {
        if (!element.hasAttribute(DIALOG_TABINDEX_ATTR)) return;

        const previousTabindex = element.getAttribute(DIALOG_TABINDEX_ATTR);
        if (previousTabindex === "") {
          element.removeAttribute("tabindex");
        } else {
          element.setAttribute("tabindex", previousTabindex);
        }

        element.removeAttribute(DIALOG_TABINDEX_ATTR);
        return;
      }

      if (element.hasAttribute(DIALOG_TABINDEX_ATTR)) return;

      element.setAttribute(
        DIALOG_TABINDEX_ATTR,
        element.getAttribute("tabindex") ?? "",
      );
      element.setAttribute("tabindex", "-1");
    },
  );
}

function temporaryHideFocusVisible() {
  document.body.classList.add("no-focus-visible");
  setTimeout(() => {
    document.body.classList.remove("no-focus-visible");
  }, 200);
}

function deactivateAccountDialog(account) {
  account.dataset.dbiAccountOpen = "false";
  cancelPendingFocus(account);
  setDialogFocusablesTabbable(account, false);
}

function activateAccountDialog(account) {
  account.dataset.dbiAccountOpen = "true";
  setDialogFocusablesTabbable(account, true);
}

function rememberAccountOpener(account) {
  const state = getAccountState(account);
  const active = document.activeElement;
  const trigger = getAccountTrigger(account);

  if (active instanceof HTMLElement) {
    const root = getAccountShadowRoot(account);
    if (root?.contains(active) || account.contains(active)) {
      state.opener =
        active.closest("button, [role='button']") || trigger || account;
      return;
    }
  }

  state.opener = trigger || account;
}

function ensureAccountOpener(account) {
  const state = getAccountState(account);
  if (state.opener && isNodeConnected(state.opener)) return;

  rememberAccountOpener(account);

  if (!state.opener || !isNodeConnected(state.opener)) {
    state.opener = getAccountTrigger(account) || account;
  }
}

function focusAccountTarget(account, target) {
  if (!target || !isNodeConnected(target)) return false;

  prepareOpenerForFocus(target);

  if (
    target instanceof HTMLElement &&
    target.getAttribute("tabindex") === "-1" &&
    target.matches("button, a[href], input, select, textarea")
  ) {
    target.removeAttribute("tabindex");
  }

  try {
    target.focus({ preventScroll: true });
  } catch {
    try {
      target.focus();
    } catch {
      return false;
    }
  }

  return isFocusOnTarget(target, account);
}

function restoreAccountFocus(account) {
  const state = getAccountState(account);
  const opener = getAccountFocusTarget(account, state.opener);

  deactivateAccountDialog(account);

  if (!opener) {
    state.opener = null;
    cancelPendingRestore(account);
    return;
  }

  if (state.restoringFocus) return;

  state.restoringFocus = true;

  const finishRestore = (success) => {
    if (success) {
      state.opener = null;
      temporaryHideFocusVisible();
    }

    cancelPendingRestore(account);
  };

  const tryRestore = () => {
    if (!isNodeConnected(opener)) {
      finishRestore(false);
      return true;
    }

    if (isAccountDialogVisible(account)) return false;

    if (focusAccountTarget(account, opener)) {
      finishRestore(true);
      return true;
    }

    return false;
  };

  const scheduleRestoreAttempt = (delay) => {
    const timer = setTimeout(() => {
      tryRestore();
    }, delay);

    state.restoreTimers.push(timer);
  };

  const runRestoreAttempts = () => {
    if (tryRestore()) {
      finishRestore(true);
      return;
    }

    RESTORE_RETRY_DELAYS.forEach(scheduleRestoreAttempt);
  };

  requestAnimationFrame(() => {
    requestAnimationFrame(runRestoreAttempts);
  });
}

function focusAccountDialog(account) {
  const root = getAccountShadowRoot(account);
  if (!root || !isAccountDialogActive(account)) return;

  const state = getAccountState(account);
  cancelPendingFocus(account);

  const applyFocus = () => {
    if (!isAccountDialogActive(account)) return;

    const closeButton = findDialogCloseButton(root);
    const focusTarget =
      closeButton || getAccountDialogFocusables(account)[0] || null;
    focusTarget?.focus({ preventScroll: true });
  };

  state.focusRaf = requestAnimationFrame(() => {
    applyFocus();
    state.focusTimer = setTimeout(applyFocus, 50);
  });
}

function getOpenAccount() {
  return (
    Array.from(document.querySelectorAll("shopify-account")).find(
      isAccountDialogActive,
    ) || null
  );
}

function handleAccountTabKey(event) {
  if (event.key !== "Tab") return;

  const account = getOpenAccount();
  if (!account) return;

  const root = getAccountShadowRoot(account);
  if (hasVisibleAccountIframe(root)) return;

  const focusables = getAccountDialogFocusables(account);
  if (!focusables.length) return;

  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const active = document.activeElement;

  if (!isFocusInsideAccountDialog(account, active)) {
    event.preventDefault();
    first.focus({ preventScroll: true });
    return;
  }

  if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus({ preventScroll: true });
    return;
  }

  if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus({ preventScroll: true });
  }
}

function handleAccountFocusIn(event) {
  const account = getOpenAccount();
  if (!account) return;

  const root = getAccountShadowRoot(account);
  if (hasVisibleAccountIframe(root)) return;

  if (isFocusInsideAccountDialog(account, event.target)) return;

  event.preventDefault();
  event.stopPropagation();

  const focusables = getAccountDialogFocusables(account);
  focusables[0]?.focus({ preventScroll: true });
}

function bindGlobalAccountFocusHandlers() {
  if (document.body.dataset.dbiAccountFocusBound === "true") return;
  document.body.dataset.dbiAccountFocusBound = "true";

  document.addEventListener("keydown", handleAccountTabKey, true);
  document.addEventListener("focusin", handleAccountFocusIn, true);
}

function isCloseButtonEvent(root, event) {
  const closeButton = findDialogCloseButton(root);
  return Boolean(closeButton && event.composedPath().includes(closeButton));
}

function handleAccountDialogDismiss(account) {
  ensureAccountOpener(account);
  restoreAccountFocus(account);
}

function bindShadowCloseFallback(account) {
  const root = getAccountShadowRoot(account);
  const state = getAccountState(account);
  if (!root || state.shadowCloseBound) return;

  state.shadowCloseBound = true;

  root.addEventListener(
    "click",
    (event) => {
      if (!isCloseButtonEvent(root, event)) return;
      handleAccountDialogDismiss(account);
    },
    true,
  );

  root.addEventListener(
    "keydown",
    (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      if (!isCloseButtonEvent(root, event)) return;
      handleAccountDialogDismiss(account);
    },
    true,
  );
}

function watchAccountDialogVisibility(account) {
  const root = getAccountShadowRoot(account);
  const state = getAccountState(account);
  if (!root || state.dialogWatchBound) return;

  state.dialogWatchBound = true;

  let wasVisible = isAccountDialogVisible(account);

  const observer = new MutationObserver(() => {
    const isVisible = isAccountSheetVisible(account);

    if (
      wasVisible &&
      !isVisible &&
      !hasVisibleAccountIframe(root) &&
      (account.dataset.dbiAccountOpen === "true" ||
        getAccountState(account).opener)
    ) {
      handleAccountDialogDismiss(account);
    }

    wasVisible = isVisible;
  });

  observer.observe(root, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["hidden", "style", "class", "aria-hidden", "open"],
  });
}

function watchAccountShadowRoot(account) {
  const bindShadowHandlers = () => {
    if (!account.shadowRoot) return false;

    bindShadowCloseFallback(account);
    watchAccountDialogVisibility(account);
    return true;
  };

  if (bindShadowHandlers()) return;

  const shadowObserver = new MutationObserver(() => {
    if (bindShadowHandlers()) shadowObserver.disconnect();
  });

  shadowObserver.observe(account, { childList: true });
}

function setupAccountFocus(account) {
  bindGlobalAccountFocusHandlers();

  if (account.dataset.dbiAccountFocusListener === "true") return;
  account.dataset.dbiAccountFocusListener = "true";

  account.addEventListener(
    "pointerdown",
    (event) => {
      const trigger = getAccountTrigger(account);
      if (trigger && event.composedPath().includes(trigger)) {
        getAccountState(account).opener = trigger;
      }
    },
    true,
  );

  account.addEventListener(
    "keydown",
    (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;

      const trigger = getAccountTrigger(account);
      if (trigger && event.composedPath().includes(trigger)) {
        getAccountState(account).opener = trigger;
      }
    },
    true,
  );

  account.addEventListener("open", () => {
    cancelPendingRestore(account);
    activateAccountDialog(account);
    rememberAccountOpener(account);
    focusAccountDialog(account);
  });

  account.addEventListener("close", () => {
    handleAccountDialogDismiss(account);
  });

  watchAccountShadowRoot(account);
}

function mutationAddsAccountElement(mutations) {
  return mutations.some((mutation) =>
    Array.from(mutation.addedNodes).some((node) => {
      if (!(node instanceof Element)) return false;
      return (
        node.tagName === "SHOPIFY-ACCOUNT" ||
        Boolean(node.querySelector?.("shopify-account"))
      );
    }),
  );
}

function initAccountFocus() {
  document.querySelectorAll("shopify-account").forEach(setupAccountFocus);

  const observer = new MutationObserver((mutations) => {
    if (!mutationAddsAccountElement(mutations)) return;

    document.querySelectorAll("shopify-account").forEach(setupAccountFocus);
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initAccountFocus);
} else {
  initAccountFocus();
}
